import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { assertSnapshotActive, isSnapshotCancelled, isRetryableSnapshotError, snapshotMatchesAvatar } from '@/lib/avatarSnapshotStorage';
import { Sentry } from '@/lib/sentry';
/**
 * Sexo do perfil, normalizado. Morava em `AvatarPortal` (removido em 08/08,
 * quando o orb antigo saiu do produto inteiro) e passou a viver aqui porque
 * este hook é o único consumidor real do tipo. A ENERGIA não usa o sexo — o
 * visual sempre foi idêntico entre gêneros, e só o aria-label variava.
 */
export type PortalGender = 'male' | 'female' | 'other' | null;

// Priority cascade for the user's 2D profile picture.
//
// 1. Pre-rendered avatar → derive companion PNG (same path, .glb → .png).
//    Synchronous, no fetch. PNG companions live at
//    avatars/prerendered/{male|female}-{1..3}.png (sourced from MetaPerson
//    Creator native preview export — transparent bg, 1024×1024, head+bust).
// 2. Persisted snapshot in user_profiles.avatar_snapshot_url (future
//    server-side render path; null for all users today).
// 3. Client-side three.js render via generateAvatarSnapshot — non-iOS-Safari
//    only (Sentry VYV-FRONTEND-C: getShaderPrecisionFormat NULL on iPhone
//    Safari). Best-effort, wrapped in try/catch, reports to Sentry on
//    failure with the avatar_snapshot_render_failed tag.
// 4. Fallback sem imagem (snapshotUrl = null, isFallback = true).
//    O consumidor renderiza a energia (VyvEnergy) no lugar de um <img>.
//    Mantém a decisão de onboarding de 13/05 — nada de silhueta humanoide.

export interface SnapshotData {
  /** Real image URL when paths 1/2/3 succeed; null when isFallback=true. */
  snapshotUrl: string | null;
  /** True ONLY when paths 1/2/3 produced a real avatar PNG/JPG. */
  hasAvatar: boolean;
  /** True when the consumer should render <VyvEnergy/> instead of <img>. */
  isFallback: boolean;
  loading: boolean;
  error: string | null;
  /** Forwarded from user_profiles.sex. Kept for consumers that label by gender. */
  sex: PortalGender;
}

const DEFAULT_RESULT: SnapshotData = {
  snapshotUrl: null,
  hasAvatar: false,
  isFallback: false,
  loading: false,
  error: null,
  sex: null,
};

// All async work belongs to a cache generation. Invalidation aborts the old
// generation, including timers, so a late render cannot repopulate stale data.
const cache = new Map<string, SnapshotData>();
const inFlight = new Map<string, { promise: Promise<SnapshotData>; controller: AbortController }>();
const retryWanted = new Set<string>();
const bgRetryDone = new Set<string>();
const listeners = new Set<() => void>();
let generation = 0;
const BG_RETRY_DELAY_MS = 12000;
const RESOLVE_TIMEOUT_MS = 10000;
const HEAD_CHECK_TIMEOUT_MS = 5000;

// iOS Safari detection. Matches AvatarCore.tsx:198-201 exactly — including
// Edge-on-iOS (EdgiOS) in the exclusion list. We skip path 3 on iOS Safari
// because the WebGL context crashes (Sentry VYV-FRONTEND-C).
function isIOSMobileSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) &&
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|EdgiOS/.test(ua)
  );
}

function normalizeSex(raw: unknown): PortalGender {
  if (raw === 'male' || raw === 'female' || raw === 'other') return raw;
  return null;
}

// Pre-render existence check on the GLB public URL.
// Outcomes are deliberately split (VYV-FRONTEND-10):
//   'stale'   → HTTP 400/404: Supabase Storage answers 400 for missing public
//               objects — the DB pointer references an object that no longer
//               exists. Safe to clear.
//   'ok'      → 2xx: object exists, proceed to render.
//   'unknown' → other statuses OR fetch threw (network down / timeout /
//               offline). Stale confirmed ≠ bad network: NEVER clear on
//               'unknown', just skip the render this pass (it would fail
//               against the same network anyway) and keep the orb fallback.
// Exported (12/06): useAvatar runs the same guard on the ACTIVE avatar GLB so
// the frozen viewers never receive a dead URL.
export async function checkGlbExists(url: string): Promise<'ok' | 'stale' | 'unknown'> {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), HEAD_CHECK_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch(url, { method: 'HEAD', signal: controller.signal });
    } finally {
      clearTimeout(id);
    }
    if (res.ok) return 'ok';
    if (res.status === 400 || res.status === 404) return 'stale';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

// Stale GLB pointer confirmed → canonical "no avatar" reset, the same shape
// useAvatarLibrary.deleteAvatar applies when no active avatar remains, so
// every consumer falls back to the portal instead of retrying a dead GLB on
// each page load (the VYV-FRONTEND-10 / VYV-FRONTEND-N loop).
export async function clearStaleAvatarReference(userId: string, glbUrl: string): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .update({
      avatar_url: null,
      avatar_provider: 'none',
      avatar_source: null,
      avatar_type: 'placeholder',
      avatar_snapshot_url: null,
    })
    .eq('user_id', userId)
    .eq('avatar_url', glbUrl);
  if (error) {
    console.error('[useAvatarSnapshot] stale reference cleanup failed:', error);
    return;
  }
  console.warn('[useAvatarSnapshot] cleared stale avatar reference:', glbUrl);
  try {
    Sentry.captureMessage('avatar_stale_reference_cleared', {
      level: 'warning',
      tags: { feature: 'avatar_snapshot' },
      extra: { glbUrl },
    });
  } catch {
    // Sentry not initialized in dev — swallow.
  }
}

async function resolveSnapshot(userId: string, signal: AbortSignal): Promise<SnapshotData> {
  try {
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('avatar_url, avatar_provider, avatar_snapshot_url, sex')
      .eq('user_id', userId)
      .abortSignal(signal)
      .maybeSingle();
    assertSnapshotActive(signal);

    if (error) {
      console.error('[useAvatarSnapshot] profile fetch failed:', error);
      return { ...DEFAULT_RESULT, isFallback: true, error: 'Profile fetch failed' };
    }

    const p = profile as {
      avatar_url?: string | null;
      avatar_provider?: string | null;
      avatar_snapshot_url?: string | null;
      sex?: string | null;
    } | null;

    const avatarUrl = p?.avatar_url ?? null;
    const provider = p?.avatar_provider ?? 'none';
    const existingSnapshot = p?.avatar_snapshot_url ?? null;
    const sex = normalizeSex(p?.sex);

    // Path 1 — pre-rendered companion PNG. Pattern-match on the GLB path:
    // avatars/prerendered/male-1.glb → avatars/prerendered/male-1.png.
    if (avatarUrl && avatarUrl.includes('/prerendered/')) {
      return {
        snapshotUrl: avatarUrl.replace(/\.glb$/i, '.png'),
        hasAvatar: true,
        isFallback: false,
        loading: false,
        error: null,
        sex,
      };
    }

    // Path 2 — persisted server-side snapshot.
    if (avatarUrl && existingSnapshot && await snapshotMatchesAvatar(existingSnapshot, avatarUrl)) {
      assertSnapshotActive(signal);
      return {
        snapshotUrl: existingSnapshot,
        hasAvatar: true,
        isFallback: false,
        loading: false,
        error: null,
        sex,
      };
    }

    // Path 3 — client-side three.js render. Skipped on ALL native platforms
    // (Capacitor WKWebView / Android WebView) and iOS mobile Safari. The
    // WebGL context there either crashes (VYV-FRONTEND-C:
    // getShaderPrecisionFormat NULL) or hangs without settling — the latter
    // wedged `loading` true forever, producing the infinite avatar spinner on
    // the profile / Identity Card (VYV-FRONTEND-P). The isIOSMobileSafari UA
    // check alone misses the Capacitor WKWebView (its UA carries no "Safari"
    // token), so we gate on Capacitor.isNativePlatform() as well. On these
    // platforms we go straight to the orb fallback; the real snapshot is a
    // future server-side render job (tracked separately).
    const skipClientRender = Capacitor.isNativePlatform() || isIOSMobileSafari();
    if (avatarUrl && provider !== 'none' && skipClientRender) {
      // BY-DESIGN skip, now VISIBLE (12/06): on iOS (native + mobile Safari)
      // the client render never runs, so an iOS-only user NEVER gets a
      // snapshot — "zero snapshots + zero errors in 3h" was this silent
      // branch, not a failure. The breadcrumb makes the platform skip
      // countable in Sentry; the real fix is the future server-side render.
      try {
        Sentry.addBreadcrumb({
          category: 'avatar_snapshot',
          message: 'render_skipped_platform',
          level: 'info',
          data: { native: Capacitor.isNativePlatform(), iosSafari: isIOSMobileSafari() },
        });
      } catch { /* Sentry not initialized in dev */ }
    }
    if (avatarUrl && provider !== 'none' && !skipClientRender) {
      // HEAD the GLB BEFORE spinning up three.js (VYV-FRONTEND-10: profiles
      // pointing at deleted generated/*.glb objects made every page load
      // re-attempt the render against a 400). Stale (400/404) → clear the
      // pointer and fall to the orb. Network failure → breadcrumb only,
      // current visual fallback, NO destructive write.
      const glbStatus = await checkGlbExists(avatarUrl);
      assertSnapshotActive(signal);
      if (glbStatus === 'stale') {
        await clearStaleAvatarReference(userId, avatarUrl);
        return {
          snapshotUrl: null,
          hasAvatar: false,
          isFallback: true,
          loading: false,
          error: null,
          sex,
        };
      }
      if (glbStatus === 'unknown') {
        try {
          Sentry.addBreadcrumb({
            category: 'avatar_snapshot',
            message: 'glb_head_check_inconclusive',
            level: 'warning',
            data: { glbUrl: avatarUrl },
          });
        } catch {
          // Sentry not initialized in dev — swallow.
        }
        console.warn('[useAvatarSnapshot] GLB HEAD check inconclusive — skipping render this pass');
        return {
          snapshotUrl: null,
          hasAvatar: false,
          isFallback: true,
          loading: false,
          error: null,
          sex,
        };
      }
      try {
        // Post-W4 Avaturn deprecation: provider can never be 'avaturn'
        // anymore; isAvaturn arg pinned false (Z-up rotation branch in
        // generateAvatarSnapshot is now dead but kept for asset-orient
        // backwards-compat if any legacy GLBs surface).
        // Timeout-guarded so a hung WebGL/loader path can never leave the
        // spinner spinning — it rejects and falls through to the orb below.
        // Keep Three.js, its renderer and FBX loader out of metadata/auth paths.
        const { generateAvatarSnapshot } = await import('@/lib/generateAvatarSnapshot');
        assertSnapshotActive(signal);
        const newUrl = await generateAvatarSnapshot(avatarUrl, userId, false, signal);
        if (newUrl) {
          return {
            snapshotUrl: newUrl,
            hasAvatar: true,
            isFallback: false,
            loading: false,
            error: null,
            sex,
          };
        }
      } catch (genErr) {
        if (isSnapshotCancelled(genErr)) throw genErr;
        // Breadcrumb only — generateAvatarSnapshot already captureException's
        // the REAL underlying error before re-throwing its generic wrapper
        // (generateAvatarSnapshot.ts catch block). Capturing the wrapper here
        // too double-counted every failure as two Sentry issues
        // (VYV-FRONTEND-10 + VYV-FRONTEND-N).
        try {
          Sentry.addBreadcrumb({
            category: 'avatar_snapshot',
            message: 'snapshot_render_failed_fallback_to_portal',
            level: 'error',
            data: { provider, sex },
          });
        } catch {
          // Sentry not initialized in dev — swallow.
        }
        console.warn('[useAvatarSnapshot] generation failed:', genErr);
        // Mark for ONE background retry (the hook schedules it) — a transient
        // GLB-CDN hiccup right after creation shouldn't leave the profile on
        // the fallback until the next full page load.
        if (isRetryableSnapshotError(genErr)) retryWanted.add(userId);
        // fall through to portal
      }
    }

    // Path 4 — fallback sem imagem. snapshotUrl null; o consumidor ramifica
    // em isFallback para renderizar <VyvEnergy state="idle" />.
    return {
      snapshotUrl: null,
      hasAvatar: false,
      isFallback: true,
      loading: false,
      error: null,
      sex,
    };
  } catch (err) {
    if (isSnapshotCancelled(err)) return { ...DEFAULT_RESULT, isFallback: true };
    console.warn('[useAvatarSnapshot] resolveSnapshot error:', err);
    return { ...DEFAULT_RESULT, isFallback: true, error: 'Snapshot resolve failed' };
  }
}

function loadSnapshot(userId: string, epoch: number): Promise<SnapshotData> {
  const existing = inFlight.get(userId);
  if (existing) return existing.promise;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<SnapshotData>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      if (generation === epoch) retryWanted.add(userId);
      if (generation === epoch) {
        Sentry.captureMessage('avatar_snapshot_resolve_timeout', { level: 'warning', tags: { feature: 'avatar_snapshot' } });
      }
      resolve({ ...DEFAULT_RESULT, isFallback: true });
    }, RESOLVE_TIMEOUT_MS);
  });
  const promise = Promise.race([resolveSnapshot(userId, controller.signal), timeout])
    .then((result) => {
      if (generation === epoch) cache.set(userId, result);
      return result;
    }).finally(() => {
      clearTimeout(timer);
      if (inFlight.get(userId)?.controller === controller) inFlight.delete(userId);
    });
  inFlight.set(userId, { promise, controller });
  return promise;
}

export function useAvatarSnapshot(): SnapshotData {
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<SnapshotData>({ ...DEFAULT_RESULT, loading: true });
  useEffect(() => {
    const onInvalidate = () => setRevision((value) => value + 1);
    listeners.add(onInvalidate);
    return () => { listeners.delete(onInvalidate); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const epoch = generation;
    const current = () => !cancelled && generation === epoch;
    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!current()) return;
        const userId = session?.user.id;
        if (!userId) {
          setData({ ...DEFAULT_RESULT, isFallback: true });
          return;
        }
        const result = cache.get(userId) ?? await loadSnapshot(userId, epoch);
        if (!current()) return;
        setData(result);
        if (retryWanted.delete(userId) && !bgRetryDone.has(userId)) {
          bgRetryDone.add(userId);
          retryTimer = setTimeout(() => {
            if (!current()) return;
            cache.delete(userId);
            void loadSnapshot(userId, epoch).then((fresh) => {
              if (current()) setData(fresh);
            });
          }, BG_RETRY_DELAY_MS);
        }
      } catch (error) {
        if (current()) setData({ ...DEFAULT_RESULT, isFallback: true });
        if (!isSnapshotCancelled(error)) console.warn('[useAvatarSnapshot] resolve failed:', error);
      }
    })();
    return () => { cancelled = true; clearTimeout(retryTimer); };
  }, [revision]);
  return data;
}

export function invalidateSnapshotCache(): void {
  generation += 1;
  for (const { controller } of inFlight.values()) controller.abort();
  inFlight.clear();
  cache.clear();
  retryWanted.clear();
  bgRetryDone.clear();
  for (const listener of listeners) listener();
}
